<template>
  <div class="cw-select"
       :class="{
        'opened': opened,
      }"
       v-click-outside="() => toggleOpenList(false)"
  >
    <slot name="label"/>
    <div
      class="cw-select__title w-100 text-left"
      @click="toggleOpenList(!opened)"
      ref="title"
    >
      <div class="d-flex flex-wrap gap-05 flex-grow-1">
        <div v-for="selectedOption in optionModel"
             :key="selectedOption.id"
             :class="{
             'cw-selected__option': multiselect,
           }"
        >
          <span>{{ selectedOption?.[propForName] }}</span>
          <span
            v-if="multiselect"
            class="cw-close"
            @click.stop.prevent="removeOption(selectedOption)"
          >
            X
          </span>
        </div>
      </div>
      <div class="cw-select__angle d-flex align-items-center justify-content-center">
        <single-angle/>
      </div>
    </div>
    <div v-show="opened" class="cw-select__list flex-column" :class="position">
      <label
        v-for="({option, level}, index) in list"
        :key="`${option[propForValue]}_${option.id||option.alias}`"
        class="d-flex gap-1 text-left align-items-baseline cw-select__option"
        :class="{'selected': isSelected(option)}"
        :data-focused-index="index"
        :style="{'--l': level}"
      >
        <input :type="type"
               :value="option[propForValue]"
               :name="name"
               @input="onUpdate(option, $event)"
               :checked="isSelected(option)"
               class="d-none"
        />
        <slot :option="option"/>
      </label>
    </div>
  </div>
</template>

<script>
import SingleAngle from '@/assets/img/singleAngle';

export default {
  name: 'SelectComponent',
  components: {SingleAngle},
  props: {
    name: {
      type: String,
      default: 'name',
    },
    multiselect: {
      type: Boolean,
      default: false,
    },
    optionsList: {
      type: Array,
      required: true,
    },
    propForName: {
      type: String,
      default: 'name',
    },
    propForValue: {
      type: String,
      default: 'id',
    },
    withEmpty: {
      type: Boolean,
      default: true,
    },
    selectedValue: {
      type: [String, Number],
      default: null,
    },
    caseSensitive: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      optionModel: this.findModelByPropForValue(this.selectedValue),
      opened: false,
      isMounted: false,
    };
  },
  computed: {
    type() {
      return this.multiselect ? 'checkbox' : 'radio';
    },
    list() {
      const EMPTY = {
        option: {
          [this.propForName]: '',
          [this.propForValue]: null,
        },
      };

      return [...(this.withEmpty ? [EMPTY] : []), ...this.getFlattenOptionsList()];
    },
    position() {
      if (!this.opened) return '';
      return this.$refs?.title?.getBoundingClientRect()?.top > window.innerHeight * 0.55 ?
        'bottom-100' : 'top-100';
    },
  },
  watch: {
    optionModel() {
      !this.multiselect && this.opened && this.toggleOpenList(false);
      this.$emit('update:selectedValue', this.optionModel);
    },
    selectedValue(newId) {
      this.optionModel = this.findModelByPropForValue(newId);
    },
  },
  methods: {
    getFlattenOptionsList() {
      const flattedList = [];
      const flatChildren = (list, level = 1) => list.forEach((option) => {
        const needShow = option.hasOwnProperty('is_displayable') ?
          Number(option.is_displayable) :
          1;
        needShow && flattedList.push({
          option,
          level,
        });
        option.children?.length && flatChildren(option.children, level + needShow);
      });

      flatChildren(this.optionsList);
      return flattedList;
    },
    findModelByPropForValue(searchableProp) {
      return (this.list || this.getFlattenOptionsList()).filter(({option: item}) => {
        return this.caseSensitive ? String(item[this.propForValue]) === String(searchableProp) :
          String(item[this.propForValue] || '').toLowerCase() ===
          String(searchableProp).toLowerCase();
      }).map(({option}) => option);
    },
    toggleOpenList(bool) {
      this.opened = bool;
    },
    onUpdate(data, event) {
      const checked = event.target.checked;
      checked ? this.addOption(data) : this.removeOption(data);
    },
    addOption(data) {
      if (this.multiselect) {
        this.optionModel.push(data);
      } else {
        this.optionModel = [data];
      }
    },
    removeOption(data) {
      this.optionModel = this.optionModel.filter((option) => {
        return option[this.propForValue] !== data[this.propForValue];
      });
    },
    isSelected(option) {
      return this.optionModel.find((model) => {
        return model[this.propForValue] === option[this.propForValue];
      });
    },
  },
  mounted() {
    this.isMounted = true;
  },
};
</script>

<style scoped lang="scss">
.cw-select {
  --min-option-height: 1.5rem;
  position: relative;
  width: 100%;
  display: flex;
  min-width: 10rem;
  font-weight: 400;

  &.opened {
    .cw-select__angle {
      transform: rotate(90deg);
    }
  }
}

.cw-select_viewport {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.cw-select__title {
  overflow: hidden;
  position: relative;
  cursor: pointer;
  margin: 0;
  line-height: 1.5;
  min-height: 100%;
  gap: .5rem;
  display: flex;
  align-items: center;
  color: var(--select-title-color, var(--settings-body-text-color));

  .cw-select__angle {
    height: 2rem;
    width: 2rem;
    transform: rotate(-90deg);
    transition: transform .2s linear;

    svg {
      width: 50%;
      height: 50%;
      color: var(--select-title-color, var(--settings-footer-background-70));
    }
  }
}

.cw-selected__option {
  padding: .3rem .5rem;
  border: 1px solid var(--settings-footer-background-50);
  border-radius: 1rem;
  text-rendering: auto;
  -webkit-font-smoothing: antialiased;
  writing-mode: horizontal-tb;
  display: flex;

  span.cw-close {
    margin-left: 1rem;
    padding: 0 .5rem;
    font-weight: 400;
    font-style: normal;
    border-radius: 50%;
    overflow: hidden;
    transition: all .15s linear;
    &:hover{
      background: var(--settings-body-text-color-20);
    }
  }
}

.cw-select__list {
  display: flex;
  position: absolute;
  left: 0;
  z-index: 2;
  width: 100%;
  max-height: 40vh;
  overflow: auto;
  background-color: var(--settings-body-background);
  box-shadow: var(--card-shadow);
}

.top-100 {
  top: 100%;
}

.bottom-100 {
  bottom: 100%;
}

.cw-select__option {
  padding: .5rem .25rem .5rem calc(0.5rem * var(--l, 1));
  font-weight: 400;
  border-bottom: 1px solid var(--border-color, transparent);
  box-sizing: content-box;
  margin: 0;
  > * {
    min-height: var(--min-option-height);
  }
  &.selected {
    background-color: var(--settings-footer-background-20);
  }
}

</style>
